import React, { useState, useEffect } from 'react';
import ApiBasedProfileCard, { TalentCardData } from '../components/TalentProfile/ApiBasedProfileCard';
import { typographyClasses } from '../styles/typography';

const ApiBasedProfileTest: React.FC = () => {
  const [talentCards, setTalentCards] = useState<TalentCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTalentCards = async () => {
      try {
        setLoading(true);
        const response = await fetch('http://localhost:8000/api/talent-cards');
        if (!response.ok) {
          throw new Error('Failed to fetch talent cards');
        }
        const data = await response.json();
        setTalentCards(data.talent_cards || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchTalentCards();
  }, []);

  const handleDelete = (id: string) => {
    setTalentCards(prev => prev.filter(card => card.id !== id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className={`${typographyClasses.heading.h3} text-red-800 mb-2`}>Error</h2>
            <p className={`${typographyClasses.body.large} text-red-700`}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className={`${typographyClasses.heading.h1} mb-4`}>API-Based Profile Cards</h1>
          <p className={`${typographyClasses.body.large} text-gray-600 mb-2`}>
            This page demonstrates the new ApiBasedProfileCard component that exclusively uses data from the /api/talent-cards endpoint.
          </p>
          <p className={`${typographyClasses.body.small} text-gray-500`}>
            Found {talentCards.length} talent cards
          </p>
        </div>

        {talentCards.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <h3 className={`${typographyClasses.heading.h3} mb-2`}>No Talent Cards Found</h3>
            <p className={`${typographyClasses.body.large} text-gray-600`}>
              No talent cards are available from the API endpoint.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {talentCards.map((talentCard) => (
              <div key={talentCard.id} className="bg-white rounded-lg shadow-sm border p-6">
                <div className="mb-4">
                  <h2 className={`${typographyClasses.heading.h3} mb-1`}>{talentCard.employee_name}</h2>
                  <p className={`${typographyClasses.body.small} text-gray-500`}>ID: {talentCard.id}</p>
                </div>
                <ApiBasedProfileCard 
                  talentData={talentCard}
                  showRadarChart={true}
                  onDelete={handleDelete}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiBasedProfileTest;